import gql from 'graphql-tag';
import {
  SalesData,
  SalesDataStub,
  OrganizationId,
  IInterval,
  Category,
  SalesByCategorySummaryData,
  SalesByCategorySummaryDataStub,
  SalesByCategorySummary,
  SalesByCategorySummaryStub,
  SalesByCategoryData,
  SalesByCategoryDataStub,
  SalesByProductData,
  SalesByProductDataStub,
  ISalesByCategorySummaryData,
  SalesByProductConnection,
  SalesByProductOrder,
} from '@kaizenplatform/intelligence-entity';
import GraphQLClient from '../../adapter/GraphQLClient';
import sleep from '../../utility/sleep';

class SalesWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public salesStub = async (): Promise<SalesData> => {
    await sleep(2000);
    return new SalesDataStub();
  };

  public sales = async (organizationId: OrganizationId, periods: IInterval[]): Promise<SalesData> => {
    const result = await this.graphqlClient.query(
      gql`
        query sales($organizationId: ID!, $periods: [IntervalInput!]!) {
          sales(organizationId: $organizationId, periods: $periods) {
            daily {
              label
              values {
                datetime
                value
              }
            }
            weekly {
              label
              values {
                datetime
                value
              }
            }
            monthly {
              label
              values {
                datetime
                value
              }
            }
          }
        }
      `,
      {
        organizationId,
        periods,
      },
    );
    return SalesData.fromJSON(result.sales);
  };

  public salesByVisitorStub = async (): Promise<SalesData> => {
    await sleep(2000);
    return new SalesDataStub();
  };

  public salesByVisitor = async (organizationId: OrganizationId, periods: IInterval[]): Promise<SalesData> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByVisitor($organizationId: ID!, $periods: [IntervalInput!]!) {
          salesByVisitor(organizationId: $organizationId, periods: $periods) {
            daily {
              label
              values {
                datetime
                value
              }
            }
            weekly {
              label
              values {
                datetime
                value
              }
            }
            monthly {
              label
              values {
                datetime
                value
              }
            }
          }
        }
      `,
      {
        organizationId,
        periods,
      },
    );
    return SalesData.fromJSON(result.salesByVisitor);
  };

  public salesByCategorySummaryStub = async (): Promise<SalesByCategorySummaryData[]> => {
    await sleep(2000);
    return [
      new SalesByCategorySummaryDataStub({
        summaries: [
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 3000000,
              category: Category.fromJSON({ name: '少年漫画', id: 'abc' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 4000000,
              category: Category.fromJSON({ name: '少女漫画', id: 'def' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 2500000,
              category: Category.fromJSON({ name: '小説', id: 'def' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 3300000,
              category: Category.fromJSON({ name: 'ビジネス', id: 'def' }),
            }),
          ),
        ],
        label: 'current',
      }),
      new SalesByCategorySummaryDataStub({
        summaries: [
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 2000000,
              category: Category.fromJSON({ name: '少年漫画', id: 'abc' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 3000000,
              category: Category.fromJSON({ name: '少女漫画', id: 'def' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 1500000,
              category: Category.fromJSON({ name: '小説', id: 'def' }),
            }),
          ),
          SalesByCategorySummary.fromJSON(
            new SalesByCategorySummaryStub({
              value: 2300000,
              category: Category.fromJSON({ name: 'ビジネス', id: 'def' }),
            }),
          ),
        ],
        label: 'previous',
      }),
    ];
  };

  public salesByCategorySummaries = async (
    organizationId: OrganizationId,
    periods: IInterval[],
    // category?: Category,
  ): Promise<SalesByCategorySummaryData[]> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByCategorySummaries($organizationId: ID!, $periods: [IntervalInput!]!) {
          salesByCategorySummaries(organizationId: $organizationId, periods: $periods) {
            summaries {
              category {
                id
                name
              }
              value
            }
            label
          }
        }
      `,
      {
        organizationId,
        periods,
        // category,
      },
    );

    return result.salesByCategorySummaries.map((data: ISalesByCategorySummaryData) =>
      SalesByCategorySummaryData.fromJSON(data),
    );
  };

  public salesByCategoryStub = async (): Promise<SalesByCategoryData> => {
    await sleep(2000);
    return new SalesByCategoryDataStub();
  };

  public salesByCategory = async (
    organizationId: OrganizationId,
    periods: IInterval[],
    // category?: Category,
  ): Promise<SalesByCategoryData> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByCategory($organizationId: ID!, $periods: [IntervalInput!]!) {
          salesByCategory(organizationId: $organizationId, periods: $periods) {
            daily {
              label
              values {
                datetime
                value
              }
              category {
                id
                name
              }
            }
            weekly {
              label
              values {
                datetime
                value
              }
              category {
                id
                name
              }
            }
            monthly {
              label
              values {
                datetime
                value
              }
              category {
                id
                name
              }
            }
          }
        }
      `,
      {
        organizationId,
        periods,
        // category,
      },
    );

    return SalesByCategoryData.fromJSON(result.salesByCategory);
  };

  public salesByProduct = async (
    organizationId: OrganizationId,
    period: IInterval,
    first?: number,
    last?: number,
    after?: string,
    before?: string,
    order?: SalesByProductOrder,
    // category?: Category,
  ): Promise<SalesByProductConnection> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByProduct(
          $organizationId: ID!
          $period: IntervalInput!
          $first: Int
          $last: Int
          $after: String
          $before: String
          $order: SalesByProductOrder
        ) {
          salesByProduct(
            organizationId: $organizationId
            period: $period
            first: $first
            last: $last
            after: $after
            before: $before
            order: $order
          ) {
            edges {
              cursor
              node {
                product {
                  id
                  name
                  category {
                    id
                    name
                  }
                }
                value
                quantity
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
            }
          }
        }
      `,
      {
        organizationId,
        period,
        first,
        last,
        before,
        after,
        order,
        // category,
      },
    );
    return SalesByProductConnection.fromJSON(result.salesByProduct);
    // return result.salesByProductData.map((data: ISalesByProductData) => SalesByProductData.fromJSON(data));
  };

  public salesByProductAfter = async (
    organizationId: OrganizationId,
    period: IInterval,
    first?: number,
    last?: number,
    after?: string,
    // category?: Category,
  ): Promise<SalesByProductConnection> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByProduct($organizationId: ID!, $period: IntervalInput!, $first: Int, $last: Int, $after: String) {
          salesByProduct(organizationId: $organizationId, period: $period, first: $first, last: $last, after: $after) {
            edges {
              cursor
              node {
                product {
                  id
                  name
                  category {
                    id
                    name
                  }
                }
                value
                quantity
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
            }
          }
        }
      `,
      {
        organizationId,
        period,
        first,
        last,
        after,
        // category,
      },
    );

    return SalesByProductConnection.fromJSON(result.salesByProduct);
    // return result.salesByProductData.map((data: ISalesByProductData) => SalesByProductData.fromJSON(data));
  };

  public salesByProductBefore = async (
    organizationId: OrganizationId,
    period: IInterval,
    first?: number,
    last?: number,
    before?: string,
    // category?: Category,
  ): Promise<SalesByProductConnection> => {
    const result = await this.graphqlClient.query(
      gql`
        query salesByProduct($organizationId: ID!, $period: IntervalInput!, $first: Int, $last: Int, $before: String) {
          salesByProduct(
            organizationId: $organizationId
            period: $period
            first: $first
            last: $last
            before: $before
          ) {
            edges {
              cursor
              node {
                product {
                  id
                  name
                  category {
                    id
                    name
                  }
                }
                value
                quantity
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
            }
          }
        }
      `,
      {
        organizationId,
        period,
        first,
        last,
        before,
        // category,
      },
    );

    return SalesByProductConnection.fromJSON(result.salesByProduct);
    // return result.salesByProductData.map((data: ISalesByProductData) => SalesByProductData.fromJSON(data));
  };

  public salesByProductStub = async (): Promise<SalesByProductData[]> => {
    await sleep(2000);
    return [
      new SalesByProductDataStub(),
      new SalesByProductDataStub(),
      new SalesByProductDataStub(),
      new SalesByProductDataStub(),
    ];
  };
}

export default SalesWebApi;
